import { useEffect, useMemo, useRef, useState } from "react"

const AUTO_CLOSE_TIMEOUT_MS = 3000
const HOVER_SLOWDOWN_FACTOR = 2

const TYPES = {
  notice: {
    role: "status",
    iconClass: "jawnaspoldzielnia-flash__icon-frame--notice",
    progressClass: "jawnaspoldzielnia-flash__progress-bar--notice",
    icon: (
      <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
        <path fill="currentColor" d="M8 1.5a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13Zm3.06 5.03-3.5 3.5a.75.75 0 0 1-1.06 0l-1.56-1.56a.75.75 0 1 1 1.06-1.06l1.03 1.03L10 5.47a.75.75 0 1 1 1.06 1.06Z" />
      </svg>
    ),
  },
  alert: {
    role: "alert",
    iconClass: "jawnaspoldzielnia-flash__icon-frame--alert",
    progressClass: "jawnaspoldzielnia-flash__progress-bar--alert",
    icon: (
      <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
        <path fill="currentColor" d="M8 1.5a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13ZM7.25 4.75a.75.75 0 0 1 1.5 0v3.5a.75.75 0 0 1-1.5 0v-3.5ZM8 11.75a.875.875 0 1 1 0-1.75.875.875 0 0 1 0 1.75Z" />
      </svg>
    ),
  },
}

const FALLBACK_TYPE = {
  role: "status",
  iconClass: "",
  progressClass: "",
  icon: (
    <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <path fill="currentColor" d="M8 1.5a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13ZM7.25 7.25A.75.75 0 0 1 8 6.5h.25a.75.75 0 0 1 .75.75v3a.75.75 0 0 1-1.5 0V8a.75.75 0 0 1 .75-.75ZM8 5.5a.875.875 0 1 1 0-1.75.875.875 0 0 1 0 1.75Z" />
    </svg>
  ),
}

export default function FlashMessages({ flash, labels, placement = "default" }) {
  const messages = useMemo(() => flattenFlash(flash), [flash])
  const [dismissed, setDismissed] = useState(new Set())

  useEffect(() => {
    setDismissed(new Set())
  }, [messages])

  const visibleMessages = messages.filter((message) => !dismissed.has(message.id))
  const stackClassName = [
    "pointer-events-none fixed right-4 top-4 z-50 grid w-[min(24rem,calc(100vw-2rem))] gap-3",
    placement === "admin" ? "jawnaspoldzielnia-flash-stack--admin" : "",
  ].filter(Boolean).join(" ")

  return (
    <div id="flash">
      <div id="flash_messages" aria-live="polite" className={stackClassName}>
        {visibleMessages.map((message) => (
          <FlashMessage
            key={message.id}
            message={message}
            closeLabel={labels.close}
            onClose={() => setDismissed((current) => new Set(current).add(message.id))}
          />
        ))}
      </div>
    </div>
  )
}

function FlashMessage({ message, closeLabel, onClose }) {
  const [progress, setProgress] = useState(1)
  const hoveringRef = useRef(false)
  const variant = TYPES[message.type] || FALLBACK_TYPE

  useEffect(() => {
    let elapsed = 0
    let frame
    let lastFrameAt = performance.now()

    const tick = (now) => {
      const frameDelta = now - lastFrameAt
      lastFrameAt = now
      elapsed += frameDelta / (hoveringRef.current ? HOVER_SLOWDOWN_FACTOR : 1)

      const nextProgress = Math.max(0, 1 - elapsed / AUTO_CLOSE_TIMEOUT_MS)
      setProgress(nextProgress)

      if (nextProgress > 0) {
        frame = requestAnimationFrame(tick)
      } else {
        onClose()
      }
    }

    frame = requestAnimationFrame(tick)

    return () => cancelAnimationFrame(frame)
  }, [onClose])

  return (
    <div
      role={variant.role}
      data-controller="flash-message"
      data-flash-message-timeout-value={AUTO_CLOSE_TIMEOUT_MS}
      className="jawnaspoldzielnia-flash"
      onPointerEnter={() => {
        hoveringRef.current = true
      }}
      onPointerLeave={() => {
        hoveringRef.current = false
      }}
    >
      <div className="flex items-start gap-3">
        <div className={`jawnaspoldzielnia-flash__icon-frame ${variant.iconClass}`} aria-hidden="true">
          <span className="jawnaspoldzielnia-flash__icon">{variant.icon}</span>
        </div>
        <p className="m-0 flex-1 leading-6">{message.message}</p>
        <button
          type="button"
          aria-label={closeLabel}
          className="-mr-1 flex size-7 shrink-0 items-center justify-center rounded-full text-[var(--color-ink-muted)] transition hover:bg-[var(--color-surface-subtle)] hover:text-[var(--color-ink)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)]"
          onClick={onClose}
        >
          <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
            <path fill="currentColor" d="M4.22 4.22a.75.75 0 0 1 1.06 0L8 6.94l2.72-2.72a.75.75 0 1 1 1.06 1.06L9.06 8l2.72 2.72a.75.75 0 1 1-1.06 1.06L8 9.06l-2.72 2.72a.75.75 0 0 1-1.06-1.06L6.94 8 4.22 5.28a.75.75 0 0 1 0-1.06Z" />
          </svg>
        </button>
      </div>
      <div className="mt-3 h-1 overflow-hidden rounded-full bg-[var(--color-surface-subtle)]">
        <div
          data-flash-message-target="progress"
          className={`jawnaspoldzielnia-flash__progress-bar ${variant.progressClass}`}
          style={{ transform: `scaleX(${progress})` }}
        />
      </div>
    </div>
  )
}

function flattenFlash(flash) {
  return Object.entries(flash || {}).flatMap(([type, value]) => {
    const values = Array.isArray(value) ? value : [value]

    return values
      .filter(Boolean)
      .map((message, index) => ({
        id: `${type}-${index}-${message}`,
        type,
        message,
      }))
  })
}
